// ============================================================
// AdminPanel.service.ts
// Shared RTK Query service for the entire admin-panel module.
// Covers ALL sub-routes: entrance-class, orientation,
// orientation-attendance, dynamic-attendance, KallooriKanavu, etc.
//
// Angular sources:
//   - AdminPanelService (admin-panel.service.ts)
//   - TpdService (tpdmodule/tpd.service.ts) — teacher details
//   - ApprovalService (Approval/approval.service.ts) — block list
// ============================================================
import { apiSlice } from '../../store/api/apiSlice'; // adjust path

// ── Types ────────────────────────────────────────────────────

export interface EntranceClassRow {
  id: number;
  dist_id: string;
  district_name: string;
  block_id: string;
  block_name: string;
  teacher_id: string;
  teacher_name: string;
  udise_code: string | null;
  school_name: string | null;
}

export interface BlockItem {
  BlockId: number;
  BlockName: string;
}

export interface TeacherDetail {
  TchrName: string;
  SchlName: string | null;
  SchlId: string;
}

export interface ApiResponse<T> {
  dataStatus: boolean;
  message: string;
  result: T;
}

export interface ApiResponseRecords<T> {
  dataStatus: boolean;
  message: string;
  records: T;
}

// ── Orientation Attendance Types ──────────────────────────────

export interface OrientationStudent {
  id: string;
  student_id: string;
  StudEmisId: string;
  Studname: string;
  Gender: string;
  block_name: string;
  udise_code: string;
  school_name: string;
  atten_status: string | null;
}

export interface AttendanceRecord {
  id: string;
  student_id: string;
  atten_status: string;
  atten_date: string;
}

// ── Endpoints ────────────────────────────────────────────────

export const adminPanelApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Entrance Class ─────────────────────────────────────

    // GET list of entrance class teachers
    getEntranceClassList: builder.mutation<
      ApiResponse<EntranceClassRow[]>,
      { Type: string }
    >({
      query: (data) => ({
        url: 'EntranceExamTchrDetailsGet',
        method: 'POST',
        body: data,
        service: 'djangoTnEmis',
      }),
    }),

    // Save new entrance class teacher
    saveEntranceClassTeacher: builder.mutation<
      ApiResponse<null>,
      { records: Record<string, any> }
    >({
      query: (data) => ({
        url: 'EntranceExamTchrDetails',
        method: 'POST',
        body: data,
        service: 'djangoTnEmis',
      }),
    }),

    // Delete entrance class teacher
    removeEntranceClassTeacher: builder.mutation<
      ApiResponse<null>,
      { records: { IndxId: number } }
    >({
      query: (data) => ({
        url: 'RemoveTeachrDetails',
        method: 'POST',
        body: data,
        service: 'djangoTnEmis',
      }),
    }),

    // Get blocks by district
    getBlocksByDistrict: builder.query<ApiResponseRecords<BlockItem[]>, number>(
      {
        query: (distId) => ({
          url: `DistBlockListGet?distId=${distId}`,
          method: 'GET',
          service: 'djangoTnEmis',
        }),
      }
    ),

    // Verify teacher by ID
    verifyTeacher: builder.query<ApiResponse<TeacherDetail[]>, string>({
      query: (tchrId) => ({
        url: `GetTchrDetails?TchrId=${tchrId}`,
        method: 'GET',
        service: 'djangoTnEmis',
      }),
    }),

    // ─── Orientation Attendance ─────────────────────────────

    // Get student list for attendance
    // Angular: admin.getStuList(data)
    // POST https://tng2c2.tnschools.gov.in/emis3APICode/CGWillingStudList
    getOrientationStudentList: builder.mutation<
      ApiResponse<OrientationStudent[]>,
      { TchrId: number; Type: string; Attdate: string }
    >({
      query: (data) => ({
        url: 'CGWillingStudList',
        method: 'POST',
        body: data,
        service: 'djangoTnEmis',
      }),
    }),

    // Save attendance
    // Angular: admin.stuAttend(data)
    // POST https://tng2c2.tnschools.gov.in/emis3APICode/CGStudAttendanceUpdt
    saveOrientationAttendance: builder.mutation<
      ApiResponse<null>,
      { records: AttendanceRecord[] }
    >({
      query: (data) => ({
        url: 'CGStudAttendanceUpdt',
        method: 'POST',
        body: data,
        service: 'djangoTnEmis',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  // Entrance Class
  useGetEntranceClassListMutation,
  useSaveEntranceClassTeacherMutation,
  useRemoveEntranceClassTeacherMutation,
  useLazyGetBlocksByDistrictQuery,
  useLazyVerifyTeacherQuery,
  // Orientation Attendance
  useGetOrientationStudentListMutation,
  useSaveOrientationAttendanceMutation,
} = adminPanelApi;
